import * as React from "react";
import { ExternalLink, Globe } from "lucide-react";

import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------ */
/* Document citation — circular numbered button                        */
/* ------------------------------------------------------------------ */

/**
 * Lexical-guard verification state of a citation (Spec 1a):
 * - `"verified"`   \u2014 the verbatim quote was found at the cited page.
 * - `"unverified"` \u2014 the quote could not be located (see `reason`).
 * - `undefined`    \u2014 the guard did not run (unchecked / disabled).
 */
export type CitationVerified = "verified" | "unverified" | undefined;

const REASON_LABELS: Record<string, string> = {
  quote_not_found: "quote not found on the cited page",
  wrong_page: "quote appears on a different page",
  page_out_of_range: "cited page is out of range",
  page_not_retrieved: "cited page was not retrieved",
};

export interface DocCitationIdentityProps {
  /** The display reference number, e.g. `1` renders as `[1]`. */
  refNumber: number;
  filename?: string;
  page?: number | null;
  quote?: string;
  /** Tier-1 lexical guard verdict; drives the verified/unverified chip state. */
  verified?: CitationVerified;
  /** Machine reason when unverified (e.g. `quote_not_found`). */
  reason?: string;
  size?: "sm" | "md";
  onClick?: () => void;
  className?: string;
}

const DocCitationIdentity = React.forwardRef<HTMLButtonElement, DocCitationIdentityProps>(
  ({ refNumber, filename, page, quote, verified, reason, size = "sm", onClick, className }, ref) => {
    const dim = size === "sm" ? "size-5 text-[11px]" : "size-6 text-xs";
    const titleParts: string[] = [];
    if (filename) titleParts.push(filename);
    if (page != null) titleParts.push(`p. ${page}`);
    if (quote) titleParts.push(quote);
    if (verified === "verified") titleParts.push("\u2713 verified against source");
    else if (verified === "unverified") titleParts.push(`\u26a0 unverified \u2014 ${(reason && REASON_LABELS[reason]) || reason || "could not verify"}`);
    const title = titleParts.join(" \u00b7 ") || undefined;

    const ariaLabelParts = [`Citation ${refNumber}`];
    if (filename) ariaLabelParts.push(filename);
    if (page != null) ariaLabelParts.push(`page ${page}`);
    if (verified) ariaLabelParts.push(verified);

    const stateClass =
      verified === "verified"
        ? "border-success/50 bg-success/10 text-success hover:bg-success/20"
        : verified === "unverified"
          ? "border-warning/50 bg-warning/10 text-warning hover:bg-warning/20"
          : "border-border/60 bg-muted text-muted-foreground hover:bg-accent";

    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        title={title}
        aria-label={ariaLabelParts.join(", ")}
        data-testid="citation-chip"
        data-verified={verified ?? "unchecked"}
        className={cn(
          "inline-flex items-center justify-center rounded-full border font-medium align-middle",
          stateClass,
          dim,
          className
        )}
      >
        <span aria-hidden>{refNumber}</span>
      </button>
    );
  }
);
DocCitationIdentity.displayName = "DocCitationIdentity";

/* ------------------------------------------------------------------ */
/* Web citation — external-link chip                                   */
/* ------------------------------------------------------------------ */

export interface WebCitationIdentityProps {
  refNumber?: number | null;
  url: string;
  title?: string;
  snippet?: string;
  retrievedAt?: string;
  size?: "sm" | "md";
  className?: string;
}

const WebCitationIdentity = React.forwardRef<HTMLSpanElement, WebCitationIdentityProps>(
  ({ refNumber, url, title, snippet, retrievedAt, size = "sm", className }, ref) => {
    const label = title || url;
    const isHttps = url.startsWith("https://");
    const dim = size === "sm" ? "text-[11px]" : "text-xs";
    const iconDim = size === "sm" ? "size-3" : "size-3.5";

    const tooltipParts: string[] = [label];
    if (snippet) tooltipParts.push(snippet);
    if (retrievedAt) tooltipParts.push(`Retrieved: ${retrievedAt}`);

    return (
      <span
        ref={ref}
        className={cn(
          "mx-0.5 inline-flex items-center gap-1 rounded-full bg-info/10 px-2 py-0.5 font-medium text-info align-middle",
          dim,
          className
        )}
        title={tooltipParts.join("\n")}
      >
        <Globe className={iconDim} aria-hidden />
        {isHttps ? (
          <a href={url} target="_blank" rel="noopener noreferrer" className="underline">
            {refNumber != null ? String(refNumber) : "web"}
          </a>
        ) : (
          <span>{refNumber != null ? String(refNumber) : "web"}</span>
        )}
        {size === "md" && isHttps ? <ExternalLink className="size-3" aria-hidden /> : null}
      </span>
    );
  }
);
WebCitationIdentity.displayName = "WebCitationIdentity";

export { DocCitationIdentity, WebCitationIdentity };
